import React, { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useIsAuthenticated } from '@azure/msal-react';
import Hotjar from '@hotjar/browser';
import SettingsIcon from '@mui/icons-material/Settings';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import InfoIcon from '@mui/icons-material/Info';
import ContactPageIcon from '@mui/icons-material/ContactPage';
import { Email } from '@mui/icons-material';
import AutoModeIcon from '@mui/icons-material/AutoMode';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import { ClientIcon } from '@/components/icons/Client';
import { TaskIcon } from '@/components/icons/TaskIcon';
import { NewBusinessIcon } from '@/components/icons/NewBusinessIcon';
import { ClaimsIcon } from '@/components/icons/ClaimsIcon';
import { EndorsementIcon } from '@/components/icons/Endorsement';
import { RenewalIcon } from '@/components/icons/Renewal';
import { RelationshipsIcon } from '@/components/icons/RelationshipsIcon';
import { AuditIcon } from '@/components/icons/AuditIcon';
import { openDialog } from '@/features/dialog/dialogSlice';
import { useFeatureFlagContext } from '@/context/featureFlagContext';
import { IS_PROD } from '@/utils/environment';
import { paths } from '@/app/paths';
import { SideNavSection } from '../SideNavSection';

export const CrmSideNavSections = () => {
	const dispatch = useDispatch();
	const isAuthenticated = useIsAuthenticated();

	const { getFeatureFlags } = useFeatureFlagContext();
	const showUnlayerEmails = Boolean(getFeatureFlags('unlayerEmails')?.active);
	const showUnlayerDocuments = Boolean(getFeatureFlags('unlayerDocuments')?.active);

	const homeList = useMemo(() => {
		const list = [
			{
				text: 'Clients',
				icon: (
					<ClientIcon
						width='30px'
						height='30px'
						bgColor='transparent'
						sx={{ ml: '-3px' }}
					/>
				),
				link: paths.home,
			},
			{
				text: 'Prospects',
				icon: <ContactPageIcon width='30px' height='30px' />,
				link: paths.prospects,
			},
			{
				text: 'Relationships',
				icon: (
					<RelationshipsIcon
						width='30px'
						height='30px'
						bgColor='transparent'
					/>
				),
				link: paths.relationships,
			},
			{
				text: 'Tasks',
				icon: <TaskIcon width='30px' height='30px' bgColor='transparent' />,
				link: paths.tasks,
			},
			{
				text: 'Audit',
				icon: <AuditIcon width='30px' height='30px' bgColor='transparent' />,
				link: paths.audit,
			},
		];
		return isAuthenticated ? list : list.slice(0, -2);
	}, [isAuthenticated]);

	const pipelinesList = useMemo(() => {
		const list = [
			{
				text: 'New Business',
				icon: <NewBusinessIcon width='30px' height='30px' bgColor='transparent' />,
				link: paths.newBusiness,
			},
			{
				text: 'Claims',
				icon: <ClaimsIcon width='30px' height='30px' bgColor='transparent' />,
				link: paths.claims,
			},
			{
				text: 'Endorsements',
				icon: <EndorsementIcon width='30px' height='30px' bgColor='transparent' />,
				link: paths.endorsements,
			},
			{
				text: 'Renewals',
				icon: <RenewalIcon width='30px' height='30px' bgColor='transparent' />,
				link: paths.renewals,
			},
			{ text: 'Debtors', icon: <PaidOutlinedIcon />, link: paths.debtors },
		];

		return list;
	}, []);

	const myAccountList = useMemo(() => {
		if (!isAuthenticated) return [];
		return [
			{
				text: 'Settings',
				icon: <SettingsIcon width='30px' height='30px' />,
				link: paths.settings,
			},
			...(!showUnlayerEmails
				? [
						{
							text: 'Templates',
							icon: <Email width='30px' height='30px' />,
							link: paths.templates,
						},
					]
				: []),
			...(showUnlayerEmails
				? [
						{
							text: 'Templates',
							icon: <Email width='30px' height='30px' />,
							link: paths.unlayerTemplates,
						},
					]
				: []),
			...(showUnlayerDocuments
				? [
						{
							text: 'Document Templates',
							icon: <ArticleOutlinedIcon width='30px' height='30px' />,
							link: paths.unlayerDocumentTemplates,
						},
					]
				: []),
			{
				text: 'Automations',
				icon: <AutoModeIcon width='30px' height='30px' />,
				link: paths.automations,
			},
		];
	}, [isAuthenticated, showUnlayerEmails, showUnlayerDocuments]);

	const myResourcesList = useMemo(() => {
		if (!isAuthenticated) return [];
		return [
			{
				text: 'Contact Support',
				icon: <InfoIcon width='30px' height='30px' />,
				external: false,
				onClick: () => {
					if (IS_PROD) {
						Hotjar.event('report_issue_clicked');
					}
					dispatch(openDialog({ dialogName: 'issueReporting' }));
				},
			},
		];
	}, [dispatch, isAuthenticated]);

	return (
		<>
			<SideNavSection items={homeList} />
			{isAuthenticated && <SideNavSection title='Pipelines' items={pipelinesList} />}
			<SideNavSection title='Resources' items={myResourcesList} />
			<SideNavSection title='Account' items={myAccountList} />
		</>
	);
};
